import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Anchor,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";

import { Can, EmptyMessage, ManagementLayout } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";
import { notifyUserFacingError } from "@util/toErrorMessage";
import UserOrganizationAssociate from "./UserOrganizationAssociate";
import { fetchUserOrganizations } from "./userOrganizationsApi";

export default function UserOrganizationManagement({
  userId,
  firstName = "",
  onSuccess = () => {},
}) {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managing, setManaging] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const loadOrganizations = useCallback(() => {
    if (!userId) return;

    setLoading(true);
    fetchUserOrganizations(userId)
      .then((rows) => {
        setOrganizations(rows);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setOrganizations([]);
        notifyUserFacingError(err, toast, {
          source: "user_organization_management_list",
        });
      });
  }, [userId]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  function afterMembershipChange() {
    setManaging(false);
    loadOrganizations();
    onSuccess();
  }

  function confirmRemove() {
    if (!pendingRemove?.id || !pendingRemove?.organization_user_id) return;

    setRemoving(true);
    axios
      .post(
        `/organizations/${pendingRemove.id}/users/${pendingRemove.organization_user_id}/inactivate/`,
        {}
      )
      .then(() => {
        setRemoving(false);
        setPendingRemove(null);
        toast.success(t("updated"));
        afterMembershipChange();
      })
      .catch((err) => {
        setRemoving(false);
        notifyUserFacingError(err, toast, {
          source: "user_organization_remove",
        });
      });
  }

  let list;
  if (loading) {
    list = (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  } else if (organizations.length === 0) {
    list = (
      <EmptyMessage
        title={t("user_not_assigned_to_organization")}
        text={t("user_not_assigned_to_organization")}
      />
    );
  } else {
    list = (
      <Card>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("cs:organization_singular")}</Table.Th>
              <Table.Th>{t("status")}</Table.Th>
              <Table.Th>{t("organization_admin_plural")}</Table.Th>
              <Table.Th>{t("manager_plural")}</Table.Th>
              <Table.Th style={{ width: 200 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {organizations.map((organization) => (
              <Table.Tr key={organization.id}>
                <Table.Td>
                  <Anchor
                    component={Link}
                    to={`/users/${userId}/organizations/${organization.id}`}
                    size="sm"
                  >
                    {organization.name || organization.id}
                  </Anchor>
                </Table.Td>
                <Table.Td>
                  {organization.status_formatted || "—"}
                </Table.Td>
                <Table.Td>
                  {organization.is_org_admin ? t("active") : t("inactive")}
                </Table.Td>
                <Table.Td>
                  {organization.is_org_manager ? t("active") : t("inactive")}
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="flex-end">
                    <Button
                      size="xs"
                      variant="light"
                      component={Link}
                      to={`/users/${userId}/organizations/${organization.id}`}
                    >
                      {t("manage_membership")}
                    </Button>
                    <Can permission={PERMISSIONS.ORGANIZATION_USERS_MANAGE}>
                      <Button
                        size="xs"
                        color="red"
                        variant="light"
                        onClick={() => setPendingRemove(organization)}
                      >
                        {t("remove")}
                      </Button>
                    </Can>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    );
  }

  return (
    <>
      <ManagementLayout
        dataComponent={list}
        createComponent={
          <Can permission={PERMISSIONS.ORGANIZATION_USERS_MANAGE}>
            {!managing ? (
              <Button
                leftSection={<IconPlus size={14} />}
                onClick={() => setManaging(true)}
                fullWidth
              >
                {t("add_user_first_name_to_organization", {
                  name: firstName || t("user_singular"),
                })}
              </Button>
            ) : (
              <Card>
                <Stack gap="sm">
                  <Title order={5}>
                    {t("add_user_first_name_to_organization", {
                      name: firstName || t("user_singular"),
                    })}
                  </Title>
                  <UserOrganizationAssociate
                    userId={userId}
                    organizations={organizations}
                    onSuccess={afterMembershipChange}
                    onCancel={() => setManaging(false)}
                  />
                </Stack>
              </Card>
            )}
          </Can>
        }
      />

      <Modal
        opened={Boolean(pendingRemove)}
        onClose={() => {
          if (removing) return;
          setPendingRemove(null);
        }}
        title={t("remove_user_from_organization_title")}
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            {t("remove_user_from_organization_confirm", {
              name: firstName || t("user_singular"),
              organization:
                pendingRemove?.name ||
                pendingRemove?.id ||
                t("cs:organization_singular"),
            })}
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setPendingRemove(null)}
              disabled={removing}
            >
              {t("cancel")}
            </Button>
            <Button color="red" loading={removing} onClick={confirmRemove}>
              {t("remove")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
